import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { ArrowRight, BadgeCheck, Clock3, FileUp, ShieldAlert } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import ApplicantShell from "../components/ApplicantShell";
import config from "../config/config";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { clearStoredAuth } from "../utils/authSession";

function authHeaders() {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function formatDateTime(value) {
  if (!value) return "Keine Frist";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" });
}

function formatRequestStatus(value) {
  const normalized = String(value || "").toLowerCase();
  if (normalized === "sent") return "Gesendet";
  if (normalized === "viewed") return "Gesehen";
  if (normalized === "answered") return "Antwort eingegangen";
  if (normalized === "fulfilled") return "Erfuellt";
  if (normalized === "expired") return "Abgelaufen";
  if (normalized === "obsolete") return "Nicht mehr noetig";
  return "Entwurf";
}

function formatResponseStatus(value) {
  const normalized = String(value || "").toLowerCase();
  if (normalized === "response_received") return "Eingang noch zu pruefen";
  if (normalized === "response_under_review") return "In Pruefung";
  if (normalized === "response_accepted") return "Akzeptiert";
  if (normalized === "response_rejected") return "Zur Nacharbeit";
  if (normalized === "response_incomplete") return "Noch unvollstaendig";
  return "Noch keine Antwort";
}

function formatRelativeDays(value) {
  if (value == null || value === "") return "n/a";
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return String(value);
  if (numeric === 0) return "heute";
  if (numeric === 1) return "1 Tag";
  if (numeric > 1) return `${numeric} Tage`;
  if (numeric === -1) return "seit 1 Tag ueberfaellig";
  return `seit ${Math.abs(numeric)} Tagen ueberfaellig`;
}

function formatUrgency(value) {
  const normalized = String(value || "").toLowerCase();
  if (normalized === "critical" || normalized === "urgent") return "kritisch";
  if (normalized === "high") return "hoch";
  if (normalized === "low") return "niedrig";
  return "mittel";
}

function getRequestTimingLabel(requestEntry) {
  if (requestEntry?.overdue) return "ueberfaellig";
  if (requestEntry?.due_soon) return "bald faellig";
  if (requestEntry?.awaiting_employer_review) return "in Pruefung";
  return "im Lauf";
}

function timingTone(requestEntry) {
  if (requestEntry?.overdue) return "border-rose-200 bg-rose-50 text-rose-700";
  if (requestEntry?.due_soon) return "border-amber-200 bg-amber-50 text-amber-700";
  if (requestEntry?.awaiting_employer_review) return "border-sky-200 bg-sky-50 text-sky-700";
  return "border-slate-200 bg-slate-50 text-slate-600";
}

function buildStatusGroups(requests = []) {
  return {
    open: requests.filter((entry) => ["sent", "viewed"].includes(entry.status)),
    review: requests.filter((entry) => entry.status === "answered"),
    fulfilled: requests.filter((entry) => entry.status === "fulfilled"),
    archived: requests.filter((entry) => ["expired", "obsolete"].includes(entry.status)),
  };
}

function createDraft() {
  return {
    response_text: "",
    additional_details: "",
    acknowledged: false,
    mark_not_available: false,
    mark_already_fulfilled: false,
    completion_note: "",
    verification_reference_id: null,
    uploaded_reference_label: "",
  };
}

function buildReferencePayload(requestEntry, filePath) {
  const today = new Date().toISOString().slice(0, 10);
  return {
    userid: localStorage.getItem("userid"),
    type: "other",
    title: `Filing response: ${requestEntry.title}`,
    source: "Authority filing request",
    issued_at: today,
    reference_text: requestEntry.request_reason || requestEntry.description || requestEntry.title,
    notes: `Filing case ${requestEntry.case_id} / request ${requestEntry.request_id}`,
    status: "pending_review",
    file_path: filePath,
  };
}

function statusTone(status) {
  if (status === "fulfilled") return "border-emerald-200 bg-emerald-50 text-emerald-700";
  if (["expired", "obsolete"].includes(status)) return "border-slate-200 bg-slate-100 text-slate-600";
  if (status === "answered") return "border-sky-200 bg-sky-50 text-sky-700";
  return "border-amber-200 bg-amber-50 text-amber-700";
}

function responseTone(status) {
  if (status === "response_accepted") return "border-emerald-200 bg-emerald-50 text-emerald-700";
  if (status === "response_rejected") return "border-rose-200 bg-rose-50 text-rose-700";
  if (status === "response_incomplete") return "border-amber-200 bg-amber-50 text-amber-700";
  if (status === "response_under_review") return "border-sky-200 bg-sky-50 text-sky-700";
  return "border-slate-200 bg-slate-50 text-slate-600";
}

export default function ApplicantFilingRequests() {
  const [searchParams] = useSearchParams();
  const userid = localStorage.getItem("userid");
  const [workspace, setWorkspace] = useState({ cases: [], requests: [], summary: {}, notifications: [], notification_board: {}, delivery_board: {} });
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busyRequestId, setBusyRequestId] = useState("");

  useEffect(() => {
    document.title = "Experts4Europe | Unterlagen nachreichen";
  }, []);

  const ensureDraft = (requestId) => drafts[String(requestId)] || createDraft();

  const patchDraft = (requestId, key, value) => {
    setDrafts((current) => ({
      ...current,
      [String(requestId)]: {
        ...createDraft(),
        ...(current[String(requestId)] || {}),
        [key]: value,
      },
    }));
  };

  const resetDraft = (requestId) => {
    setDrafts((current) => ({
      ...current,
      [String(requestId)]: createDraft(),
    }));
  };

  const handleAuthError = (err) => {
    if (Number(err?.response?.status) !== 401) return false;
    clearStoredAuth();
    window.location.href = "/login";
    return true;
  };

  const loadWorkspace = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await axios.get(`${config.API_BASE_URL}/api/applicant/authority-filing/requests`, {
        params: searchParams.get("case_id") ? { case_id: searchParams.get("case_id") } : undefined,
        headers: authHeaders(),
      });
      setWorkspace(response.data || { cases: [], requests: [], summary: {}, notifications: [], notification_board: {}, delivery_board: {} });
    } catch (err) {
      if (handleAuthError(err)) return;
      setError(err?.response?.data?.message || "Die Filing-Requests konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadWorkspace();
  }, [searchParams.toString()]);

  const grouped = useMemo(() => buildStatusGroups(workspace.requests || []), [workspace.requests]);
  const spotlightRequestId = searchParams.get("request_id") || "";
  const caseById = useMemo(
    () => new Map((workspace.cases || []).map((entry) => [Number(entry.case_id), entry])),
    [workspace.cases]
  );
  const searchItems = useMemo(() => ([
    { href: "/dashboard/applicant", label: "Dashboard", description: "Mission Control und dein Stand" },
    { href: "/dashboard/applicant/filing-requests", label: "Unterlagen nachreichen", description: "Gezielte Filing-Requests beantworten" },
    { href: "/dashboard/applicant/bewerbung?tab=cv", label: "Bewerbung", description: "CV, Skills und Nachweise pflegen" },
    { href: "/dashboard/applicant/sprachtest-b1", label: "Sprachtest B1", description: "Deutschsignal weiterfuehren" },
  ]), []);

  const uploadProof = async (requestEntry, file) => {
    if (!file || !userid) return;
    setBusyRequestId(String(requestEntry.request_id));
    setError("");
    setNotice("");
    try {
      const formData = new FormData();
      formData.append("userid", userid);
      formData.append("file", file);
      const uploadRes = await axios.post(`${config.API_BASE_URL}/api/cv/verification-references/upload`, formData, {
        headers: {
          ...authHeaders(),
          "Content-Type": "multipart/form-data",
        },
      });
      const referenceRes = await axios.post(
        `${config.API_BASE_URL}/api/cv/verification-references`,
        buildReferencePayload(requestEntry, uploadRes.data?.filePath || ""),
        { headers: authHeaders() }
      );
      const reference = referenceRes.data?.reference;
      patchDraft(requestEntry.request_id, "verification_reference_id", reference?.id || null);
      patchDraft(requestEntry.request_id, "uploaded_reference_label", reference?.title || uploadRes.data?.originalName || "Nachweis hochgeladen");
      setNotice("Nachweis hochgeladen und dem Filing-Request zugeordnet.");
    } catch (err) {
      if (handleAuthError(err)) return;
      setError(err?.response?.data?.message || "Nachweis konnte nicht hochgeladen werden.");
    } finally {
      setBusyRequestId("");
    }
  };

  const submitResponse = async (requestEntry) => {
    const draft = ensureDraft(requestEntry.request_id);
    const hasMeaningfulInput = Boolean(
      draft.response_text.trim()
      || draft.additional_details.trim()
      || draft.acknowledged
      || draft.mark_not_available
      || draft.mark_already_fulfilled
      || draft.verification_reference_id
    );

    if (!hasMeaningfulInput) {
      setError("Bitte trage mindestens eine Rueckmeldung, Bestaetigung oder einen Nachweis ein.");
      return;
    }

    setBusyRequestId(String(requestEntry.request_id));
    setError("");
    setNotice("");
    try {
      const response = await axios.post(
        `${config.API_BASE_URL}/api/applicant/authority-filing/requests/${requestEntry.request_id}/responses`,
        {
          case_id: requestEntry.case_id,
          response_text: draft.response_text,
          structured_fields: {
            additional_details: draft.additional_details,
            applicant_acknowledgement: draft.acknowledged ? "confirmed" : "",
            current_availability: draft.mark_not_available ? "currently_not_available" : "",
          },
          acknowledged: draft.acknowledged,
          mark_not_available: draft.mark_not_available,
          mark_already_fulfilled: draft.mark_already_fulfilled,
          verification_reference_id: draft.verification_reference_id,
          completion_note: draft.completion_note,
        },
        { headers: authHeaders() }
      );
      setWorkspace(response.data?.workspace || workspace);
      resetDraft(requestEntry.request_id);
      setNotice(`Antwort gespeichert: ${requestEntry.title}`);
    } catch (err) {
      if (handleAuthError(err)) return;
      setError(err?.response?.data?.message || "Antwort konnte nicht gespeichert werden.");
    } finally {
      setBusyRequestId("");
    }
  };

  const renderRequestCard = (requestEntry) => {
    const draft = ensureDraft(requestEntry.request_id);
    const caseMeta = caseById.get(Number(requestEntry.case_id));
    const busy = busyRequestId === String(requestEntry.request_id);

    return (
      <article
        key={`candidate-request-${requestEntry.request_id}`}
        className={`rounded-2xl border bg-white p-5 shadow-sm ${String(requestEntry.request_id) === String(spotlightRequestId) ? "border-teal-300 ring-2 ring-teal-100" : "border-slate-200"}`}
      >
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <span className={`rounded-full border px-3 py-1 text-xs ${statusTone(requestEntry.status)}`}>
                {formatRequestStatus(requestEntry.status)}
              </span>
              <span className={`rounded-full border px-3 py-1 text-xs ${responseTone(requestEntry.response_status)}`}>
                {formatResponseStatus(requestEntry.response_status)}
              </span>
              <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs text-slate-600">
                Prioritaet {formatUrgency(requestEntry.urgency || "medium")}
              </span>
              <span className={`rounded-full border px-3 py-1 text-xs ${timingTone(requestEntry)}`}>
                {getRequestTimingLabel(requestEntry)}
              </span>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.18em] text-slate-400">
                {caseMeta?.job_title || "Filing Case"} · {caseMeta?.employer_display_name || "Arbeitgeber"}
              </p>
              <h3 className="mt-2 text-lg font-semibold text-slate-900">{requestEntry.title}</h3>
              <p className="mt-1 text-sm text-slate-600">
                {requestEntry.description || requestEntry.request_reason || "Hier fehlt noch etwas von dir fuer das Filing-Paket."}
              </p>
            </div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
            <p><strong className="text-slate-900">Fall</strong>: {caseMeta?.candidate_ref || `#${requestEntry.case_id}`}</p>
            <p><strong className="text-slate-900">Ort</strong>: {[caseMeta?.federal_state, caseMeta?.city].filter(Boolean).join(" / ") || "wird im Filing bestimmt"}</p>
            <p><strong className="text-slate-900">Frist</strong>: {formatDateTime(requestEntry.due_at)}</p>
            <p><strong className="text-slate-900">Naechste Erinnerung</strong>: {formatDateTime(requestEntry.next_reminder_at)}</p>
            <p><strong className="text-slate-900">Zuletzt erinnert</strong>: {formatDateTime(requestEntry.last_notified_at)}</p>
          </div>
        </div>

        <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
            <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Was fehlt</p>
            <p className="mt-2 font-semibold text-slate-900">{requestEntry.request_reason || requestEntry.title}</p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
            <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Was du tun kannst</p>
            <p className="mt-2">
              {requestEntry.requires_upload ? "Dokument hochladen" : "Angabe oder Bestaetigung senden"}
              {requestEntry.requires_signature ? " · Unterschrift beachten" : ""}
            </p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
            <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Naechster Schritt</p>
            <p className="mt-2">{requestEntry.requires_upload ? "Nachweis oder Kommentar einreichen" : "Rueckmeldung bestaetigen"}</p>
            <p className="mt-1 text-xs text-slate-500">Offen seit {formatRelativeDays(requestEntry.open_days)} · Rest {formatRelativeDays(requestEntry.days_remaining)}</p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
            <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Letzte Rueckmeldung</p>
            <p className="mt-2">
              {requestEntry.latest_response_summary?.preview_text || requestEntry.latest_response_summary?.response_type || "Noch nichts von dir eingegangen"}
            </p>
            <p className="mt-1 text-xs text-slate-500">Letzte Aktivitaet {formatDateTime(requestEntry.last_activity_at)} · Hinweise {requestEntry.reminder_count || 0}x</p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700 md:col-span-2 xl:col-span-4">
            <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Zustellung und Erinnerung</p>
            <p className="mt-2">Mail-Status {requestEntry.delivery_summary?.last_email_status || "nur in_app oder noch keine Mail"} · letzte Mail {formatDateTime(requestEntry.delivery_summary?.last_email_delivery_at)} · Retry {requestEntry.delivery_summary?.retry_count || 0}</p>
            {requestEntry.delivery_summary?.last_skip_reason ? (
              <p className="mt-1 text-xs text-slate-500">Mail-Fallback: {requestEntry.delivery_summary.last_skip_reason}</p>
            ) : null}
          </div>
        </div>

        {requestEntry.overdue || requestEntry.due_soon ? (
          <div className={`mt-4 rounded-xl border p-4 text-sm ${timingTone(requestEntry)}`}>
            {requestEntry.overdue
              ? "Diese Anforderung ist ueberfaellig und blockiert den naechsten Schritt. Bitte gib jetzt eine Rueckmeldung oder lade den Nachweis hoch."
              : "Diese Anforderung wird bald faellig. Wenn du schon etwas hast, kannst du es direkt hier nachreichen."}
          </div>
        ) : null}

        {requestEntry.status === "fulfilled" || ["expired", "obsolete"].includes(requestEntry.status) ? (
          <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
            {requestEntry.status === "fulfilled"
              ? "Dieser Request ist fuer das Filing-Paket erledigt."
              : "Dieser Request ist nicht mehr aktiv."}
          </div>
        ) : (
          <div className="mt-4 space-y-4">
            <div className="grid gap-4 lg:grid-cols-[1.4fr_1fr]">
              <div className="space-y-3">
                <div>
                  <Label className="applicant-workspace-label">Deine Rueckmeldung</Label>
                  <Textarea
                    rows={4}
                    value={draft.response_text}
                    onChange={(event) => patchDraft(requestEntry.request_id, "response_text", event.target.value)}
                    className="applicant-field"
                    placeholder="Beschreibe kurz, was du nachreichst oder was der Arbeitgeber wissen soll."
                  />
                </div>
                <div>
                  <Label className="applicant-workspace-label">Ergaenzende Angabe</Label>
                  <Input
                    value={draft.additional_details}
                    onChange={(event) => patchDraft(requestEntry.request_id, "additional_details", event.target.value)}
                    className="applicant-field"
                    placeholder="z. B. Ausstellungsdatum, Bearbeitungsstand oder kurze Klarstellung"
                  />
                </div>
                <div>
                  <Label className="applicant-workspace-label">Interne Notiz fuer diesen Request</Label>
                  <Input
                    value={draft.completion_note}
                    onChange={(event) => patchDraft(requestEntry.request_id, "completion_note", event.target.value)}
                    className="applicant-field"
                    placeholder="Kurze Notiz fuer den Filing-Kontext"
                  />
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Schnelle Antwort</p>
                <div className="mt-3 space-y-3 text-sm text-slate-700">
                  <label className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={draft.acknowledged}
                      onChange={(event) => patchDraft(requestEntry.request_id, "acknowledged", event.target.checked)}
                    />
                    <span>Ich habe die Anforderung gelesen und bestaetige sie.</span>
                  </label>
                  <label className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={draft.mark_not_available}
                      onChange={(event) => patchDraft(requestEntry.request_id, "mark_not_available", event.target.checked)}
                    />
                    <span>Das liegt aktuell noch nicht vor.</span>
                  </label>
                  <label className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={draft.mark_already_fulfilled}
                      onChange={(event) => patchDraft(requestEntry.request_id, "mark_already_fulfilled", event.target.checked)}
                    />
                    <span>Ich habe das schon erledigt und sende einen Nachweis mit.</span>
                  </label>
                </div>
                <div className="mt-4 rounded-xl border border-dashed border-slate-300 bg-white p-4">
                  <div className="flex items-center gap-2 text-sm font-medium text-slate-900">
                    <FileUp size={16} />
                    <span>Nachweis sicher hochladen</span>
                  </div>
                  <p className="mt-1 text-sm text-slate-600">
                    Der Upload bleibt in deinem geschuetzten Verification-Bereich. Im Filing-Case wird nur die freigegebene Referenz verwendet.
                  </p>
                  <div className="mt-3 flex flex-wrap items-center gap-3">
                    <Label className="inline-flex cursor-pointer items-center rounded-full border border-slate-200 bg-slate-50 px-4 py-2 text-sm text-slate-700">
                      {busy ? "Laedt..." : "Datei hochladen"}
                      <input
                        type="file"
                        accept=".pdf,image/png,image/jpeg,image/webp"
                        className="hidden"
                        onChange={(event) => uploadProof(requestEntry, event.target.files?.[0])}
                      />
                    </Label>
                    {draft.uploaded_reference_label ? (
                      <span className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs text-emerald-700">
                        {draft.uploaded_reference_label}
                      </span>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <ShieldAlert size={16} />
                <span>Kein Chat, kein Rohdokument-Archiv: nur gezielte Antwort fuer diesen Filing-Request.</span>
              </div>
              <Button className="applicant-cta-button" onClick={() => submitResponse(requestEntry)} disabled={busy}>
                {busy ? "Speichert..." : "Antwort speichern"}
              </Button>
            </div>
          </div>
        )}
      </article>
    );
  };

  return (
    <ApplicantShell
      title="Unterlagen nachreichen"
      subtitle="Hier siehst du nur die filing-spezifischen Unterlagen und Angaben, die fuer den naechsten Behoerdenschritt noch von dir gebraucht werden."
      actions={
        <>
          <Button variant="outline" className="applicant-cta-button applicant-cta-button-secondary" onClick={loadWorkspace} disabled={loading}>
            Aktualisieren
          </Button>
          <Button className="applicant-cta-button" onClick={() => (window.location.href = "/dashboard/applicant/bewerbung?tab=cv")}>
            Bewerbung oeffnen
          </Button>
        </>
      }
      notifications={workspace.notifications || []}
      searchItems={searchItems}
    >
      <div className="applicant-workspace-page applicant-workspace-page--application">
        {error ? <div className="dashboard-inline-alert dashboard-inline-alert-danger">{error}</div> : null}
        {notice ? <div className="dashboard-inline-alert dashboard-inline-alert-success">{notice}</div> : null}

        <section className="applicant-application-stage">
          <div className="applicant-application-stage__main">
            <span className="applicant-section-kicker">Filing Requests</span>
            <h2>Was von dir noch gebraucht wird</h2>
            <p>Du gibst hier nur genau das zurueck, was fuer den Filing-Fall fehlt. Das Arbeitgeber-Team sieht danach den neuen Status, nicht automatisch deine ganze Dokumentwelt.</p>
            <div className="applicant-workspace-scoreboard applicant-workspace-scoreboard--application">
              <article className="applicant-signal-tile applicant-signal-tile--application">
                <div className="applicant-signal-tile__top"><span>Offen</span><strong>{workspace.summary?.open_request_count || 0}</strong></div>
                <small>Warten direkt auf deine Rueckmeldung.</small>
              </article>
              <article className="applicant-signal-tile applicant-signal-tile--application">
                <div className="applicant-signal-tile__top"><span>In Pruefung</span><strong>{workspace.summary?.answered_request_count || 0}</strong></div>
                <small>Etwas ist eingegangen und wird geprueft.</small>
              </article>
              <article className="applicant-signal-tile applicant-signal-tile--application">
                <div className="applicant-signal-tile__top"><span>Erfuellt</span><strong>{workspace.summary?.fulfilled_request_count || 0}</strong></div>
                <small>Diese Filing-Requests sind erledigt.</small>
              </article>
              <article className="applicant-signal-tile applicant-signal-tile--application">
                <div className="applicant-signal-tile__top"><span>Review</span><strong>{workspace.summary?.response_review_count || 0}</strong></div>
                <small>Arbeitgeber prueft eingegangene Antworten.</small>
              </article>
              <article className="applicant-signal-tile applicant-signal-tile--application">
                <div className="applicant-signal-tile__top"><span>Bald faellig</span><strong>{workspace.summary?.due_soon_count || 0}</strong></div>
                <small>Diese Requests solltest du als Naechstes anfassen.</small>
              </article>
              <article className="applicant-signal-tile applicant-signal-tile--application">
                <div className="applicant-signal-tile__top"><span>Ueberfaellig</span><strong>{workspace.summary?.overdue_count || 0}</strong></div>
                <small>Hier blockiert dein Input den naechsten Filing-Schritt.</small>
              </article>
            </div>
          </div>
          <aside className="applicant-application-stage__side">
            <section className="applicant-workspace-card applicant-workspace-card--priority">
              <div className="applicant-workspace-card__head">
                <div>
                  <span className="applicant-section-kicker">Schnellueberblick</span>
                  <h3>Deine aktiven Filing-Faelle</h3>
                </div>
              </div>
              <div className="applicant-next-step-stack">
                {workspace.cases?.length ? workspace.cases.map((entry) => (
                  <div key={`case-${entry.case_id}`} className="applicant-action-list__item">
                    <div>
                      <strong>{entry.job_title || entry.profession_text || `Filing Case ${entry.case_id}`}</strong>
                      <span>{entry.employer_display_name} · {entry.workflow_stage_label || "Working Pack"} · {entry.overdue_count || 0} ueberfaellig</span>
                    </div>
                    <small>{(entry.request_counts?.sent || 0) + (entry.request_counts?.viewed || 0)}</small>
                  </div>
                )) : <div className="applicant-empty-card">{loading ? "Filing-Faelle werden geladen ..." : "Aktuell sind keine filing-spezifischen Requests fuer dich offen."}</div>}
              </div>
            </section>
            <section className="applicant-workspace-card applicant-workspace-card--priority">
              <div className="applicant-workspace-card__head">
                <div>
                  <span className="applicant-section-kicker">Hinweise</span>
                  <h3>Aktive Erinnerungen</h3>
                </div>
              </div>
              <div className="applicant-next-step-stack">
                {workspace.notification_board?.recent_notifications?.length ? workspace.notification_board.recent_notifications.slice(0, 4).map((entry) => (
                  <div key={`candidate-notification-${entry.notification_id}`} className="applicant-action-list__item">
                    <div>
                      <strong>{entry.title}</strong>
                      <span>{entry.message}</span>
                    </div>
                    <small>{formatDateTime(entry.created_at)}</small>
                  </div>
                )) : <div className="applicant-empty-card">Noch keine aktiven Filing-Hinweise.</div>}
              </div>
            </section>
          </aside>
        </section>

        <section className="space-y-6">
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Jetzt von dir noetig</p>
                <h3 className="mt-2 text-xl font-semibold text-slate-900">Offene Requests</h3>
              </div>
              <span className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs text-amber-700">
                {grouped.open.length} offen
              </span>
            </div>
            <div className="mt-4 space-y-4">
              {loading ? <div className="applicant-empty-card">Requests werden geladen ...</div> : null}
              {!loading && !grouped.open.length ? <div className="applicant-empty-card">Im Moment fehlt nichts Direktes von dir.</div> : null}
              {grouped.open.map(renderRequestCard)}
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Schon eingegangen</p>
                <h3 className="mt-2 text-xl font-semibold text-slate-900">Beantwortet / in Pruefung</h3>
              </div>
              <span className="rounded-full border border-sky-200 bg-sky-50 px-3 py-1 text-xs text-sky-700">
                {grouped.review.length} in Review
              </span>
            </div>
            <div className="mt-4 space-y-4">
              {!grouped.review.length ? <div className="applicant-empty-card">Noch keine Antwort in Pruefung.</div> : grouped.review.map(renderRequestCard)}
            </div>
          </div>

          <div className="grid gap-6 xl:grid-cols-2">
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Geschafft</p>
                  <h3 className="mt-2 text-xl font-semibold text-slate-900">Erfuellte Requests</h3>
                </div>
                <span className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs text-emerald-700">
                  <BadgeCheck size={12} className="mr-1 inline-block" />
                  {grouped.fulfilled.length}
                </span>
              </div>
              <div className="mt-4 space-y-3">
                {!grouped.fulfilled.length ? <div className="applicant-empty-card">Noch keine Filing-Requests abgeschlossen.</div> : grouped.fulfilled.map((entry) => (
                  <div key={`fulfilled-${entry.request_id}`} className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
                    <p className="font-semibold">{entry.title}</p>
                    <p className="mt-1">{entry.latest_response_summary?.preview_text || "Als erledigt im Filing-Paket verbucht."}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Nicht mehr aktiv</p>
                  <h3 className="mt-2 text-xl font-semibold text-slate-900">Abgelaufen / obsolet</h3>
                </div>
                <span className="rounded-full border border-slate-200 bg-slate-100 px-3 py-1 text-xs text-slate-600">
                  <Clock3 size={12} className="mr-1 inline-block" />
                  {grouped.archived.length}
                </span>
              </div>
              <div className="mt-4 space-y-3">
                {!grouped.archived.length ? <div className="applicant-empty-card">Keine abgelaufenen oder obsoleten Filing-Requests.</div> : grouped.archived.map((entry) => (
                  <div key={`archived-${entry.request_id}`} className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
                    <p className="font-semibold text-slate-900">{entry.title}</p>
                    <p className="mt-1">{formatRequestStatus(entry.status)} · {entry.request_reason || entry.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <section className="rounded-2xl border border-slate-200 bg-slate-50 p-5 text-sm text-slate-600">
            <div className="flex items-start gap-3">
              <ArrowRight size={16} className="mt-0.5 text-slate-400" />
              <div>
                <p className="font-semibold text-slate-900">Was hier bewusst nicht passiert</p>
                <p className="mt-1">Kein Chat, kein allgemeines Behoerdenportal, keine automatische Einreichung und keine unkontrollierte Freigabe deiner Rohdokumente. Diese Seite dient nur dazu, gezielte Filing-Anforderungen sauber zurueckzuliefern.</p>
              </div>
            </div>
          </section>
        </section>
      </div>
    </ApplicantShell>
  );
}
